import { Component, OnInit } from "@angular/core"
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin, catchError, of } from "rxjs";
import { DonorJourneySection } from "../../../models/donor-journey-section.model";
import { Note } from "../../../models/note.model";
import { DonorMetric } from "../../../models/donor-metric.model";
import { ContentService } from "../../../services/content.service";
import { NavigationService } from "../../../services/navigation.service";
import { QuestionnaireService } from "../../../services/questionnaire.service";


@Component({
  selector: "app-mrb-view",
  templateUrl: "./mrb-view.component.html",
  styleUrl: "./mrb-view.component.scss",
})
export class MrbViewComponent implements OnInit {
  userId: string | null = null;
  profileInfo: any;
  journeySections: DonorJourneySection[] = [];
  mrbSectionStatus = "";
  summaryNotes: string[] = [];
  labResults: { name: string; value: any }[] = [];
  isLoading = true;
  metrics: DonorMetric[] | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private contentService: ContentService,
    private navigationService: NavigationService,
    private questionnaireService: QuestionnaireService
  ) { }

  ngOnInit(): void {
    this.userId = this.route.parent?.snapshot.paramMap.get("userId") || null;

    if (this.userId) {
      this.loadLabResults();

      forkJoin([
        this.contentService.getDonorAndRecipientProfileInfo(this.userId).pipe(
          catchError((error) => {
            console.error("Error fetching profile info:", error);
            return of(null); // Return null or empty object if the profile info fails
          })
        ),
        this.contentService
          .getAdminDonorJourneySectionsForUser(this.userId)
          .pipe(
            catchError((error) => {
              console.error("Error fetching journey sections:", error);
              return of([]); // Return empty array if the journey sections fail
            })
          ),
        this.questionnaireService.getDonorMetrics(this.userId).pipe(
          catchError((error) => {
            console.error("Error fetching donor metrics:", error);
            return of(null);
          })
        ),
      ]).subscribe({
        next: ([profileInfo, journeySections, metrics]) => {
          // Handle the combined results
          this.profileInfo = profileInfo; // Assign profile info to a variable
          this.journeySections = journeySections; // Assign journey sections to a variable
          this.metrics = metrics; // Assign metrics to a variable
          this.updateLabResultsFromMetrics();
          this.setMrbStatus();
          this.isLoading = false; // Optional: Hide the loading indicator
        },
        error: (err) => {
          console.error("Error fetching data:", err);

          this.isLoading = false; // Optional: Hide the loading indicator even if there's an error
        },
      });
    }
  }

  setMrbStatus() {
    const mrbSection = this.journeySections.find((section) =>
      section.title.toLowerCase().includes("medical review board")
    );

    if (mrbSection) {
      this.mrbSectionStatus = mrbSection.currentStatus;
    } else {
      console.warn("Medical Review Board section not found");
    }
  }

  loadLabResults() {
    this.labResults = [
      { name: "Weight (kg)", value: "71" },
      { name: "BMI (kg/m²)", value: "29.4" },
      { name: "BSA (m²)", value: "" },
      { name: "BP Average", value: "114/74" },
      { name: "FBG (mg/dl)", value: "88" },
      { name: "HgbA1c (%)", value: "5.4" },
      { name: "Cholesterol (mg/dl)", value: "192" },
      { name: "HDL (mg/dl)", value: "57" },
      { name: "LDL (mg/dl)", value: "107" },
      { name: "Triglycerides (mg/dl)", value: "139" },
      { name: "Creatinine (mg/dl)", value: "0.78" },
      { name: "Cystatin C (mg/dl)", value: "0.68" },
      { name: "Measured GFR", value: "83" },
      { name: "Creatinine Clearance (ml/min/1.73m²)", value: "103" },
      { name: "Albumin/Creatinine Ratio (mg/g)", value: "15.4" },
      { name: "Urine RBC", value: "0" },
      { name: "CT", value: { R: "R", L: "L" } },
      { name: "Volume", value: { R: "125 48%", L: "131 52%" } },
      { name: "# Arteries", value: { R: "1", L: "1" } },
      { name: "# Veins", value: { R: "1", L: "1" } },
      { name: "Kidney Stones", value: { Type: "Calyceal tip", Size: "3mm" } },
      { name: "Kidney Mass", value: { R: "0", L: "0" } },
      { name: "EKG", value: "Normal" },
      { name: "Functional Status", value: ">10" },
      { name: "ECHO", value: "N/A" },
      { name: "Chest X-Ray", value: "Normal" },
      { name: "HCV", value: "Negative" },
      { name: "HIV", value: "Negative" },
      { name: "TB T-Spot", value: "Negative" },
      { name: "10 year ASCVD Risk", value: "0.2" },
      { name: "ESRD 20 years post donation", value: "0.34" },
    ];
  }

  private updateLabResultsFromMetrics() {
    const weightLbs =
      this.getMetricValue(["Weight", "WeightLb", "WeightLbs", "WeightPounds"]) ??
      this.profileInfo?.data?.donor?.weight;
    const bmi =
      this.getMetricValue(["BMI"]) ?? this.profileInfo?.data?.donor?.bmi;
    const bsa = this.getMetricValue(["BSA"]);

    if (weightLbs !== null) {
      const weightKg = this.convertLbsToKg(weightLbs);
      if (weightKg !== null) {
        this.setLabResultValue("Weight (kg)", weightKg);
      }
    }

    if (bmi !== null) {
      this.setLabResultValue("BMI (kg/m²)", bmi);
    }

    if (bsa !== null) {
      this.setLabResultValue("BSA (m²)", bsa);
    }
  }

  private getMetricValue(keys: string[]): string | null {
    if (!this.metrics) {
      return null;
    }

    for (const key of keys) {
      const metric = this.metrics.find((m) => m.metric_key === key);
      const value = metric?.metric_value?.toString().trim();
      if (value) {
        return value;
      }
    }

    return null;
  }

  private convertLbsToKg(value: string): string | null {
    const numericValue = Number.parseFloat(value);
    if (!Number.isFinite(numericValue)) {
      return null;
    }

    const kgValue = numericValue * 0.45359237;
    return kgValue.toFixed(1).replace(/\.0$/, "");
  }

  private setLabResultValue(name: string, value: string) {
    const target = this.labResults.find((result) => result.name === name);
    if (target) {
      target.value = value;
    }
  }

  isObject(value: any): boolean {
    return value && typeof value === "object" && !Array.isArray(value);
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  getSummaryNote(notes: Note[]) {
    // quill.getText();
    return notes.find((note) => note.noteType === "Summary")?.content;
  }

  viewNotes(section: DonorJourneySection) {
    this.router.navigate([`/hospital/donor/${this.userId}/journey`], {
      fragment: section.sectionId, // Pass section.id as a fragment for scrolling
    });
  }

  exportToPDF() {
    // const doc = new jsPDF();
    // doc.text('Summary View', 10, 10);
    // doc.save('summary.pdf');
  }

  navigateToDashboard(): void {
    this.navigationService.navigateToDashboard();
  }
}
